import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Header, Footer } from '@/components/PublicShell';
import { AnimalCard } from '@/components/AnimalCard';
import { EnquiryDialog } from '@/components/EnquiryDialog';
import { HeroPhoto } from '@/components/HeroPhoto';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';
import { HERO, GRID, BENEFITS, FAQ_TID } from '@/constants/testIds';
import { ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

export default function Home() {
  const [animals, setAnimals] = useState(null);
  const [settings, setSettings] = useState(null);
  const [category, setCategory] = useState('horse');
  const [selectedTags, setSelectedTags] = useState([]);
  const [enquiryAnimal, setEnquiryAnimal] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadAnimals = useCallback(async () => {
    try {
      const { data } = await api.get('/public/animals');
      setAnimals(data);
    } catch (e) {
      console.error('Failed to load animals:', e);
      toast.error('We could not load the herd. Please refresh the page.');
      setAnimals([]);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get('/public/settings');
      setSettings(data);
    } catch (e) {
      // Non-critical: page still renders with fallback defaults.
      console.warn('Failed to load public settings:', e);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadAnimals();
    document.title = 'Equine Patronage Program · Tollygunge Club';
  }, [loadSettings, loadAnimals]);

  const filtered = useMemo(() => {
    if (!animals) return [];
    return animals.filter((a) => a.category === category && (selectedTags.length === 0 || selectedTags.some((t) => (a.tags || []).includes(t))));
  }, [animals, category, selectedTags]);

  const toggleTag = (t) => {
    setSelectedTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  };

  const openEnquiry = (a) => {
    setEnquiryAnimal(a);
    setDialogOpen(true);
  };

  const scrollToHerd = () => document.getElementById('the-herd')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="pb-24 md:pb-0">
      <Header />
      <main>
        {/* HERO — split (text left, photo right) on desktop; stacked on mobile */}
        <section className="bg-white border-b border-border overflow-hidden">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-10 sm:py-16 md:py-20">
            <div className="grid md:grid-cols-2 md:gap-12 md:items-center">
              {/* Text column */}
              <div className="order-1 md:order-1">
                <p data-testid={HERO.eyebrow} className="text-sm sm:text-[15px] font-bold text-[#1E3A2F]">
                  {settings?.hero_eyebrow || 'Tollygunge Club Equestrian Centre · Est. 1895'}
                </p>
                <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
                  {settings?.hero_headline || 'Sponsor a Horse'}
                </h1>
                <p className="mt-6 text-[17px] sm:text-lg text-[#3E4A44] leading-relaxed max-w-xl">
                  {settings?.hero_sub}
                </p>

                {/* Mobile hero photo — shows between sub-headline and buttons */}
                <div className="mt-6 md:hidden -mx-4 sm:-mx-6">
                  <div className="aspect-[4/3] w-full overflow-hidden">
                    <HeroPhoto rounded="" />
                  </div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <Button
                    data-testid={HERO.primaryCta}
                    onClick={scrollToHerd}
                    className="btn rounded-lg bg-[#1E3A2F] hover:bg-[#16302A] h-12 px-6 text-white font-bold text-[15px]"
                  >
                    {settings?.hero_cta || 'Meet the horses'}
                    <ArrowDown className="ml-1.5 h-4 w-4" />
                  </Button>
                  <a href="#benefits" className="btn inline-flex items-center justify-center h-12 px-5 rounded-lg border border-border text-[15px] font-bold text-[#1E3A2F] hover:bg-[#F7F8F7]">
                    What&apos;s included
                  </a>
                </div>
              </div>

              {/* Desktop hero photo — fills the right half, subtle rounded inside corner */}
              <div className="order-2 hidden md:block">
                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-l-2xl md:-mr-6 lg:-mr-10">
                  <HeroPhoto rounded="rounded-l-2xl" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* THE HERD */}
        <section id="the-herd" className="bg-[#F7F8F7]">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
              <div>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Meet the horses and ponies</h2>
                <p className="mt-2 text-[15px] text-[#3E4A44]">Twenty horses and nineteen ponies at the Equestrian Centre. Pick a friend.</p>
              </div>
              <ToggleGroup
                data-testid={GRID.animalTypeToggle}
                type="single"
                value={category}
                onValueChange={(v) => v && setCategory(v)}
                className="inline-flex rounded-full bg-white border border-border p-1 self-start sm:self-auto shadow-sm"
              >
                <ToggleGroupItem data-testid={GRID.toggleHorses} value="horse" className="rounded-full px-5 h-10 text-sm font-bold data-[state=on]:bg-[#1E3A2F] data-[state=on]:text-white">
                  Horses
                </ToggleGroupItem>
                <ToggleGroupItem data-testid={GRID.togglePonies} value="pony" className="rounded-full px-5 h-10 text-sm font-bold data-[state=on]:bg-[#1E3A2F] data-[state=on]:text-white">
                  Ponies
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            {settings?.tags?.length > 0 && (
              <div data-testid={GRID.tagFilter} className="flex flex-wrap gap-2 mb-8">
                {settings.tags.map((t) => {
                  const active = selectedTags.includes(t);
                  return (
                    <button
                      key={t}
                      data-testid={GRID.tagFilterOption(t)}
                      onClick={() => toggleTag(t)}
                      className={`rounded-full px-4 h-10 text-[13px] font-bold border transition-colors min-h-[40px] ${active ? 'bg-[#1E3A2F] text-white border-[#1E3A2F]' : 'bg-white border-border text-[#3E4A44] hover:border-[#1E3A2F] hover:text-[#1E3A2F]'}`}
                    >
                      {t}
                    </button>
                  );
                })}
                {selectedTags.length > 0 && (
                  <button onClick={() => setSelectedTags([])} className="text-sm font-bold text-[#1E3A2F] underline underline-offset-4 ml-1 min-h-[40px]">Clear</button>
                )}
              </div>
            )}

            {animals === null ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {/* eslint-disable-next-line react/no-array-index-key */}
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={`skeleton-${i}`} className="rounded-xl border border-border bg-white overflow-hidden card-shadow">
                    <Skeleton className="aspect-[4/3] w-full" />
                    <div className="p-5 space-y-2">
                      <Skeleton className="h-5 w-2/3" />
                      <Skeleton className="h-3 w-1/3" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-12 w-full mt-2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-16 text-center text-muted-foreground">No animals match this filter.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {filtered.map((a) => (
                  <AnimalCard key={a.id} animal={a} onSponsor={openEnquiry} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* BENEFITS */}
        <section id="benefits" data-testid={BENEFITS.section} className="bg-white">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-14 sm:py-20">
            <div className="max-w-3xl">
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">What&apos;s included in your sponsorship</h2>
              <p className="mt-4 text-[17px] text-[#3E4A44] leading-relaxed">
                {settings?.benefits_intro || "Every horse and pony at Tolly has just one sponsor at a time, so the friendship you build is truly your own. As a sponsor you will receive:"}
              </p>
            </div>
            <ol className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5 max-w-4xl">
              {(settings?.benefits || []).map((b, i) => (
                <li key={b} className="flex items-start gap-4">
                  <span className="flex-none w-9 h-9 rounded-full bg-[#DEEFE4] text-[#1E3A2F] font-extrabold text-sm flex items-center justify-center">{i + 1}</span>
                  <p className="text-[16px] leading-relaxed text-[hsl(var(--foreground))] pt-1">{b}</p>
                </li>
              ))}
            </ol>
            {settings?.benefits_outro && (
              <p className="mt-10 max-w-3xl text-[17px] text-[#3E4A44] leading-relaxed">{settings.benefits_outro}</p>
            )}

            <div className="mt-10 rounded-xl bg-[#F7F8F7] p-6 sm:p-8 max-w-3xl">
              <p className="text-lg font-extrabold">Sponsorship pricing</p>
              <p className="mt-2 text-[16px] text-[#3E4A44] leading-relaxed">
                {settings?.pricing_note || `Sponsor a horse or a pony for ₹${(settings?.horse_price_yearly || 20000).toLocaleString('en-IN')} a year, billed through your Club account and arranged by the Stable Office.`}
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-[#F7F8F7]">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-14 sm:py-20">
            <div className="max-w-2xl mb-8">
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Frequently asked</h2>
            </div>
            <Accordion data-testid={FAQ_TID.accordion} type="single" collapsible className="rounded-xl bg-white border border-border divide-y divide-border">
              {(settings?.faq || []).map((f, i) => (
                <AccordionItem key={f.q || `faq-${i}`} value={`faq-${i}`} className="px-5 sm:px-6">
                  <AccordionTrigger className="text-left text-lg font-extrabold py-5 hover:no-underline min-h-[48px]">{f.q}</AccordionTrigger>
                  <AccordionContent className="pb-5 text-[16px] leading-relaxed text-[#3E4A44]">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      </main>
      <Footer address={settings?.footer_address} established={settings?.footer_est} />

      {/* Sticky mobile CTA */}
      <div className="sticky-cta md:hidden">
        <Button onClick={scrollToHerd} className="btn w-full rounded-lg bg-[#1E3A2F] hover:bg-[#16302A] h-12 text-white font-bold text-[15px]">
          Meet the horses
        </Button>
      </div>

      <EnquiryDialog
        animal={enquiryAnimal}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={loadAnimals}
        formIntro={settings?.form_intro}
        successTemplate={settings?.success_message_template}
      />
    </div>
  );
}
